import React from 'react';
import { AddIcon } from '../ios/IOSIcons';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { isTaskFabDraggingAtom } from '../../../../atoms/v3/taskAtoms';
import { chatSidebarCollapsedAtom, layoutModeAtom, chatSidebarWidthAtom } from '../../../../atoms/v3/uiAtoms';
import { isTouchDevice } from '../../../../utils/platformUtils';
import { trackQuickActionEvents } from '../../../../lib/analyticsEnhanced';


interface DraggableTaskButtonProps {
  onClick: () => void;
}

/**
 * 可拖拽的iOS风格任务添加按钮
 * 可以拖拽到项目区域快速创建任务
 * 也可以直接点击创建任务
 */
export const DraggableTaskButton: React.FC<DraggableTaskButtonProps> = ({ onClick }) => {
  const isFabDragging = useAtomValue(isTaskFabDraggingAtom);
  const chatSidebarCollapsed = useAtomValue(chatSidebarCollapsedAtom);
  const chatSidebarWidth = useAtomValue(chatSidebarWidthAtom);
  const layoutMode = useAtomValue(layoutModeAtom);

  // 检测是否为触摸设备 - 使用统一的平台检测函数
  const isTouch = isTouchDevice();

  // 处理点击事件
  const handleClick = () => {
    trackQuickActionEvents.quickActionClicked('fab_button', {
      is_touch: isTouch,
      layout_mode: layoutMode,
      action_type: 'click'
    });
    onClick();
  };

  // 使用dnd-kit的useDraggable钩子
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'task-fab',
    data: {
      type: 'new-task'
    }
  });

  // 计算拖拽样式
  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: isFabDragging ? 9999 : 50,
    cursor: isFabDragging ? 'grabbing' : 'pointer',
    right: layoutMode === 'desktop' && !chatSidebarCollapsed
      ? `calc(${chatSidebarWidth}rem + 1rem)`
      : '1rem'
  };

  // 使用button元素而不是div，更符合语义
  return (
    <motion.button
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      data-new-task-button
      data-testid="add-task-button"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{
        scale: isFabDragging ? 1.1 : 1,
        opacity: 1,
        boxShadow: isFabDragging ? '0 0.625rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.3)' : '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)'
      }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      className={`fixed ${layoutMode === 'desktop' ? 'bottom-8' : 'bottom-24'} w-14 h-14 rounded-full bg-ios-blue dark:bg-ios-dark-blue text-white flex items-center justify-center ${isTouch ? 'select-none' : ''}`}
      aria-label={isTouch ? "长按拖拽创建任务，点击快速创建" : "添加任务"}
      title={isTouch ? "长按拖拽创建任务，点击快速创建" : "拖拽到区域创建任务，点击快速创建"}
    >
      <div className="w-7 h-7">
        <AddIcon />
      </div>
    </motion.button>
  );
};
